import React from "react";

/**
 * The shared visual language of the Mietverhältnisse workspace.
 *
 * WHY THIS FILE. `Card`, `Field` and `Pairs` existed three times over —
 * `TenancyDetailContent`, `TenancyFinanceTab` and a hand-written `<section>`
 * in `TenancyNotesTab` — with the same classes copied between them. Three
 * copies is how Finanzen starts looking like its own product and Notizen like
 * a legacy page: not through one big divergence, but through a radius here and
 * a padding there. One definition, imported by every tab.
 *
 * PRESENTATION ONLY. Nothing here fetches, derives or decides. Each component
 * takes what it renders and renders it.
 */

/** An em dash, never an empty cell and never "undefined". */
export function show(value) {
  return value == null || value === "" ? "—" : value;
}

/**
 * A titled block.
 *
 * `subtitle` explains the block, `action` is a control belonging to it — the
 * two are laid out on one row so a card with both does not grow a third line.
 */
export function Card({ title, subtitle, action, icon, testId, children }) {
  return (
    <section
      data-testid={testId}
      className="rounded-[10px] border border-border bg-card"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-[10px]">
        <div className="flex min-w-0 items-center gap-2.5">
          {icon ? <span className="shrink-0">{icon}</span> : null}
          <div className="min-w-0">
          <h3 className="m-0 text-[13px] font-semibold text-foreground">
            {title}
          </h3>
          {subtitle ? (
            <p className="m-0 mt-[2px] text-[11.5px] leading-snug text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * Label over value.
 *
 * The label is 11.5px rather than 11px: it is the only thing naming the value
 * beneath it, and a reader who cannot resolve the label cannot read the field
 * at all. `wide` spans both columns of `Pairs` for values that are sentences
 * rather than data points.
 */
export function Field({ label, value, wide = false }) {
  return (
    <div className={wide ? "col-span-2 min-w-0" : "min-w-0"}>
      <dt className="text-[11.5px] leading-tight text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-[2px] break-words text-[13px] text-foreground">
        {show(value)}
      </dd>
    </div>
  );
}

/** The two-column field grid every data card uses. */
export function Pairs({ children }) {
  return <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>;
}

/**
 * "There is nothing here", in one shape across all seven tabs.
 *
 * Previously each tab said it differently — a dashed box in Dokumente, a bare
 * paragraph in Finanzen, an unframed block in Notizen — which made an empty
 * tab read as three different states of the product. Dashed and muted, so it
 * is legible as a container that is empty rather than as content.
 */
export function EmptyState({ title, hint, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-[10px] border border-dashed border-border bg-muted/30 px-4 py-8 text-center"
    >
      <p className="m-0 text-[13px] text-foreground">{title}</p>
      {hint ? (
        <p className="mx-auto m-0 mt-1 max-w-[46ch] text-[12.5px] leading-snug text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A secondary control: the workspace's one non-primary button shape.
 *
 * Card actions, retries and "open the other tab" all look the same on purpose
 * — none of them is the primary action of the panel they sit in.
 */
export const SECONDARY_BUTTON =
  "shrink-0 rounded-[7px] border border-border bg-card px-[10px] py-[4px] text-[12px] text-foreground hover:bg-muted disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/40";

/** The primary control inside a card — submit, and nothing else. */
export const PRIMARY_BUTTON =
  "shrink-0 rounded-[7px] border border-primary bg-primary px-[10px] py-[4px] text-[12px] font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/40";
